import { useCallback, useEffect, useState } from "react";
import { useRouter } from "@tanstack/react-router";

export const VALID_PROFILE_SECTIONS = new Set(["account", "appearance", "notifications"]);

export type ProfileSection = "account" | "appearance" | "notifications";

export function readProfileFromUrl(): ProfileSection | undefined {
  const raw = new URL(window.location.href).searchParams.get("profile");
  return typeof raw === "string" && VALID_PROFILE_SECTIONS.has(raw)
    ? (raw as ProfileSection)
    : undefined;
}

export interface UseProfileUrlReturn {
  profileSection: ProfileSection | undefined;
  profileOpen: boolean;
  setProfile: (section: ProfileSection | undefined, replace?: boolean) => void;
}

export function useProfileUrl(): UseProfileUrlReturn {
  const router = useRouter();
  const [profileSection, setProfileSection] = useState<ProfileSection | undefined>(
    readProfileFromUrl,
  );
  const profileOpen = profileSection != null;

  const setProfile = useCallback(
    (section: ProfileSection | undefined, replace = false) => {
      setProfileSection(section);
      void router.navigate({
        to: ".",
        search: ((prev: Record<string, unknown>) => ({
          ...prev,
          profile: section ?? undefined,
        })) as never,
        replace,
        viewTransition: false,
        resetScroll: false,
      });
    },
    [router],
  );

  useEffect(() => {
    const onPopState = () => {
      const section = readProfileFromUrl();
      if (!section) setProfileSection(undefined);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  return { profileSection, profileOpen, setProfile };
}
